import { useCallback, useEffect, useState } from "react";
import { climaPublica, ApiError, type ClimaFormulario } from "../api";
import { IconCheck } from "../icons";

/**
 * PESQUISA DE CLIMA ORGANIZACIONAL — a página que o COLABORADOR abre.
 *
 * É o link individual que o RH manda (WhatsApp, e-mail ou copiado): cada
 * pessoa tem o seu, em /clima/<token>. Não tem login — o token é a credencial —
 * e o formulário é o oficial da SERCAMP: setor, tempo de empresa, as
 * afirmações de 1 a 5 e a pergunta aberta do fim.
 *
 * Reaproveita a casca das outras páginas por link (classes `aval-*`).
 */

function Casca({ children, titulo }: { children: React.ReactNode; titulo?: string }) {
  return (
    <div className="aval-publica">
      <div className="aval-header">
        <span className="aval-brand">SERCAMP</span>
        <span className="aval-brand-sub">{titulo ?? "Pesquisa de Clima Organizacional"}</span>
      </div>
      <div className="aval-card">{children}</div>
      <p className="aval-rodape">RH SERCAMP · preenchimento seguro por link</p>
    </div>
  );
}

export default function ClimaPublica({ token }: { token: string }) {
  const [form, setForm] = useState<ClimaFormulario | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [setor, setSetor] = useState("");
  const [tempo, setTempo] = useState("");
  const [notas, setNotas] = useState<Record<string, number>>({});
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const dados = await climaPublica.obter(token);
      setForm(dados);
      setSetor(dados.setor ?? "");
      setTempo(dados.tempo_empresa ?? "");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a pesquisa.");
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const deEscala = (form?.perguntas ?? []).filter((p) => p.tipo === "escala");
  const respondidas = deEscala.filter((p) => notas[p.id] != null).length;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setErro(null);

    const abertaPreenchida = form.perguntas.some(
      (p) => p.tipo === "aberta" && (textos[p.id] ?? "").trim() !== "",
    );
    if (respondidas === 0 && !abertaPreenchida) {
      setErro("Marque ao menos uma resposta antes de enviar.");
      return;
    }

    setEnviando(true);
    try {
      const r = await climaPublica.enviar(token, {
        setor: setor.trim(),
        tempo_empresa: tempo,
        respostas: form.perguntas.map((p) =>
          p.tipo === "aberta"
            ? { id: p.id, resposta: textos[p.id] ?? "" }
            : { id: p.id, nota: notas[p.id] ?? null },
        ),
      });
      setEnviado(r.mensagem || form.agradecimento);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível enviar as respostas.");
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <Casca>
        <div className="empty">Abrindo a pesquisa…</div>
      </Casca>
    );
  }

  if (!form) {
    return (
      <Casca>
        <div className="aval-estado">
          <h2>Link indisponível</h2>
          <p>{erro ?? "Este link não está mais válido."}</p>
          <p>Peça um link novo ao RH da SERCAMP.</p>
        </div>
      </Casca>
    );
  }

  if (enviado) {
    return (
      <Casca titulo={form.titulo}>
        <div className="aval-estado aval-ok">
          <div className="aval-ok-icon">
            <IconCheck size={40} />
          </div>
          <h2>Resposta enviada!</h2>
          <p>{enviado}</p>
        </div>
      </Casca>
    );
  }

  if (form.respondido) {
    return (
      <Casca titulo={form.titulo}>
        <div className="aval-estado">
          <h2>Você já respondeu</h2>
          <p>Esta pesquisa já foi respondida com este link. Obrigado pela participação!</p>
        </div>
      </Casca>
    );
  }

  if (form.encerrada || form.expirado) {
    return (
      <Casca titulo={form.titulo}>
        <div className="aval-estado">
          <h2>{form.encerrada ? "Pesquisa encerrada" : "Link expirado"}</h2>
          <p>
            {form.encerrada
              ? "Esta pesquisa não está mais recebendo respostas."
              : "Este link expirou. Peça um novo ao RH."}
          </p>
        </div>
      </Casca>
    );
  }

  return (
    <Casca titulo={form.titulo}>
      <div className="aval-intro">
        <h2 style={{ margin: "0 0 4px" }}>{form.titulo}</h2>
        <p className="aval-cargo">
          {form.empresa}
          {form.periodo ? ` · ${form.periodo}` : ""}
        </p>
        <p>{form.apresentacao}</p>
        <p className="clima-aviso">
          {form.identificada ? (
            <>
              {form.colaborador ? (
                <>
                  Olá, <strong>{form.colaborador}</strong>. Este link é seu:{" "}
                </>
              ) : (
                <>Este link é individual: </>
              )}
              a sua resposta chega ao RH <strong>identificada</strong>, para que cada ponto possa ser
              tratado com você. {form.confidencialidade}
            </>
          ) : (
            <>
              Esta pesquisa é <strong>anônima</strong>: a sua resposta chega ao RH sem o seu nome —
              só o setor e o tempo de empresa. {form.confidencialidade}
            </>
          )}
        </p>
      </div>

      <form onSubmit={enviar} className="clima-form">
        <div className="clima-cabecalho">
          <label className="field">
            <span>Setor</span>
            <input
              value={setor}
              onChange={(e) => setSetor(e.target.value)}
              placeholder="Ex.: Operacional"
            />
          </label>
          <fieldset className="clima-tempo">
            <legend>Tempo de empresa</legend>
            <div className="clima-opcoes">
              {form.tempos.map((t) => (
                <button
                  type="button"
                  key={t}
                  className={`clima-opcao ${tempo === t ? "marcada" : ""}`}
                  onClick={() => setTempo(tempo === t ? "" : t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="clima-escala-guia">
          <strong>Escala de avaliação</strong>
          <div className="clima-escala-itens">
            {form.escala.map((n) => (
              <span key={n.valor}>
                <b>{n.valor}</b> {n.rotulo}
              </span>
            ))}
          </div>
        </div>

        <ol className="clima-perguntas">
          {form.perguntas.map((p) => (
            <li key={p.id} className="clima-pergunta">
              <p className="clima-pergunta-txt">{p.texto}</p>
              {p.tipo === "aberta" ? (
                <textarea
                  rows={4}
                  value={textos[p.id] ?? ""}
                  onChange={(e) => setTextos({ ...textos, [p.id]: e.target.value })}
                  placeholder="Escreva aqui a sua sugestão (opcional)"
                />
              ) : (
                <div className="clima-opcoes">
                  {form.escala.map((n) => (
                    <button
                      type="button"
                      key={n.valor}
                      className={`clima-opcao clima-nota ${notas[p.id] === n.valor ? "marcada" : ""}`}
                      onClick={() =>
                        setNotas((atuais) => {
                          const novo = { ...atuais };
                          // Clicar de novo desmarca: dá para deixar em branco,
                          // como no formulário de papel.
                          if (novo[p.id] === n.valor) delete novo[p.id];
                          else novo[p.id] = n.valor;
                          return novo;
                        })
                      }
                      title={n.rotulo}
                    >
                      <b>{n.valor}</b>
                      <small>{n.rotulo}</small>
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>

        {erro && <div className="form-error">{erro}</div>}

        <div className="aval-foot">
          <span className="aval-contador">
            {respondidas} de {deEscala.length} respondida(s)
          </span>
          <button type="submit" className="btn-primary" disabled={enviando}>
            {enviando ? "Enviando…" : "Enviar respostas"}
          </button>
        </div>
      </form>
    </Casca>
  );
}
